import type { Spec } from "@json-render/core";

/**
 * Removes child keys that are not present in `elements`. Prevents json-render
 * "Missing element X referenced as child of Y" warnings when the model lists
 * ids it never defined. Does not mutate the input.
 *
 * Returns **`spec` by reference** when nothing was stripped so callers can rely on
 * reference equality; otherwise returns a shallow clone with updated elements.
 */
export const sanitizeSpecRefs = (spec: Spec | null): Spec | null => {
  if (!spec?.elements || !spec.root) return spec;

  const keySet = new Set(Object.keys(spec.elements));
  const entries = Object.entries(spec.elements);
  let elements = spec.elements;
  let changed = false;

  for (const [key, el] of entries) {
    const children = el?.children;
    if (!Array.isArray(children) || children.length === 0) continue;
    const valid = children.filter((k) => keySet.has(k));
    if (valid.length !== children.length) {
      if (!changed) {
        elements = { ...spec.elements };
        changed = true;
      }
      elements[key] = { ...el, children: valid };
    }
  }

  return changed ? { ...spec, elements } : spec;
};

/**
 * Lists child ids that {@link sanitizeSpecRefs} would remove (per parent).
 * For devtools / tests — compares raw vs sanitized `children` arrays.
 */
export const findStrippedChildRefs = (
  raw: Spec | null,
  sanitized: Spec | null,
): { parentKey: string; strippedIds: string[] }[] => {
  if (!raw?.elements || !sanitized?.elements) return [];

  const out: { parentKey: string; strippedIds: string[] }[] = [];
  for (const key of Object.keys(raw.elements)) {
    const rawChildren = raw.elements[key]?.children ?? [];
    if (rawChildren.length === 0) continue;
    const sanChildren = new Set(sanitized.elements[key]?.children ?? []);
    const stripped = rawChildren.filter((id) => !sanChildren.has(id));
    if (stripped.length > 0)
      out.push({ parentKey: key, strippedIds: stripped });
  }
  return out;
};
